// 聊天组件API服务

import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js';
import { 
  GetMessagesResponse, 
  SendMessageResponse, 
  ApiConfig 
} from '../types';

// API服务类
export class ChatApiService {
  private config: ApiConfig;
  private pendingRequests = new Map<string, Promise<any>>();
  private requestCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5000; // 5秒缓存

  constructor(config: ApiConfig) {
    this.config = config;
  }

  // 生成签名
  private generateSignature(data: Record<string, string | number>, timestamp: number, nonce: string): string {
    const sortedData: Record<string, string | number> = {};
    Object.keys(data).sort().forEach(key => {
      sortedData[key] = data[key];
    });
    const dataStr = JSON.stringify(sortedData, null, 0);
    const signStr = `${dataStr}${timestamp}${nonce}${this.config.apiKey}`;
    return CryptoJS.SHA256(signStr).toString();
  }

  // Base64 编码数据
  private encodeData(data: Record<string, string | number>): string {
    const dataStr = JSON.stringify(data, null, 0);
    // 使用现代方式处理中文字符的 base64 编码
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(dataStr);
    let binaryString = '';
    uint8Array.forEach(byte => {
      binaryString += String.fromCharCode(byte);
    });
    return btoa(binaryString);
  }

  // 构建请求体
  private buildRequestBody(data: Record<string, string | number>) {
    const timestamp = Date.now();
    const nonce = uuidv4();
    const signature = this.generateSignature(data, timestamp, nonce);
    const encodedData = this.encodeData(data);

    return {
      data: encodedData,
      timestamp,
      nonce,
      signature
    };
  }

  // 生成请求键，用于去重和缓存
  private generateRequestKey(url: string, data: Record<string, string | number>): string {
    const sortedData = Object.keys(data).sort().reduce((acc, key) => {
      acc[key] = data[key];
      return acc;
    }, {} as Record<string, string | number>);
    
    return `${url}_${JSON.stringify(sortedData)}`;
  }

  // 检查缓存是否有效
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  // 清理过期缓存
  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, value] of this.requestCache.entries()) {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.requestCache.delete(key);
      }
    }
  }

  // 包装 fetch：支持超时与 AbortController
  private async fetchWithTimeout(url: string, options: RequestInit & { timeoutMs?: number, signal?: AbortSignal } = {}) {
    const { timeoutMs = 15000, signal, ...rest } = options;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const composedSignal = signal
      ? (new AbortController()).signal
      : undefined;
    // 如果外部也传入 signal，我们无法轻易合并，保持内部控制器即可
    try {
      const resp = await fetch(url, { ...rest, signal: controller.signal });
      return resp;
    } finally {
      clearTimeout(timeout);
    }
  }

  // 发送消息
  async sendMessage(params: {
    email: string;
    temp_user_key?: string;
    temp_name: string;
    message: string;
    from_client: string;
  }): Promise<SendMessageResponse> {
    const requestBody = this.buildRequestBody(params);
    
    const response = await this.fetchWithTimeout(this.config.sendMessageEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    const result: SendMessageResponse = await response.json();
    
    if (result.code !== 200) {
      throw new Error(result.msg || 'Failed to send message');
    }

    return result;
  }

  // 获取消息 - 添加去重和缓存
  async getMessages(params: {
    email: string;
    temp_user_key: string;
    page: number;
    pagesize: number;
  }): Promise<GetMessagesResponse> {
    const requestKey = this.generateRequestKey(this.config.getMessagesEndpoint, params);
    
    // 检查是否有正在进行的相同请求
    if (this.pendingRequests.has(requestKey)) {
      console.log('请求去重: 等待相同请求完成');
      return this.pendingRequests.get(requestKey)!;
    }
    
    // 对于分页请求，不缓存以避免分页问题
    const isPaginationRequest = params.page > 1;
    
    // 检查缓存（仅对第一页缓存）
    if (!isPaginationRequest) {
      const cached = this.requestCache.get(requestKey);
      if (cached && this.isCacheValid(cached.timestamp)) {
        console.log('请求缓存命中');
        return cached.data;
      }
    }
    
    // 清理过期缓存
    this.cleanupCache();
    
    const requestBody = this.buildRequestBody(params);
    
    // 创建请求Promise并存储
    const requestPromise = this.fetchWithTimeout(this.config.getMessagesEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const result: GetMessagesResponse = await response.json();
      
      if (result.code !== 200) {
        throw new Error(result.msg || 'Failed to fetch messages');
      }

      // 缓存结果（仅缓存第一页）
      if (!isPaginationRequest) {
        this.requestCache.set(requestKey, {
          data: result,
          timestamp: Date.now()
        });
      }

      return result;
    }).finally(() => {
      // 请求完成后移除pending状态
      this.pendingRequests.delete(requestKey);
    });
    
    // 存储pending请求
    this.pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }

  // 设置消息已读
  async setMessageRead(params: {
    email: string;
    temp_user_key: string;
    message_key: string;
  }): Promise<void> {
    const requestBody = this.buildRequestBody(params);
    
    const response = await this.fetchWithTimeout(this.config.setReadEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error('Failed to set message as read');
    }

    const result = await response.json();
    
    if (result.code !== 200) {
      throw new Error(result.msg || 'Failed to set message as read');
    }
  }
}

// 创建API服务实例的工厂函数
export function createChatApiService(config: ApiConfig): ChatApiService {
  return new ChatApiService(config);
}